import { trackFreeToolCompleted, trackFreeToolStarted } from "./analytics-free-tools";

type TrackProps = Record<string, unknown> | undefined;

function trackEventLazy(name: string, properties?: TrackProps) {
  if (typeof window === "undefined") return;
  void import("@/lib/analytics-events").then(({ trackEvent }) => trackEvent(name, properties));
}

export function trackKundaliFormStarted(props: {
  form_id?: string;
  language: string;
  source?: string;
  source_path?: string;
  first_field?: string;
}) {
  trackEventLazy("kundali_form_started", props);
  trackFreeToolStarted({
    tool_name: "free-kundali",
    form_id: props.form_id,
    language: props.language,
    source_path: props.source_path,
    placement: props.first_field,
  });
}

export function trackKundaliFormSubmitted(props: {
  attempt_id?: string;
  form_id?: string;
  language: string;
  has_time: boolean;
  source?: string;
  source_path?: string;
}) {
  trackEventLazy("kundali_form_submitted", props);
}

export function trackKundaliFormSubmitFailed(props: {
  attempt_id?: string;
  form_id?: string;
  language: string;
  has_time: boolean;
  source?: string;
  source_path?: string;
  error_key?: string;
  error_field?: string;
  error_kind?: string;
  error_message?: string;
  field_error_count?: number;
}) {
  trackEventLazy("kundali_form_submit_failed", props);
}

export type KundaliStep = "identity" | "chart" | "contact";
export type KundaliStepAction = "view" | "complete" | "back";

/**
 * Progressive-form diagnostics at journey-step grain. Deliberately accepts
 * only route context plus a stable visit occurrence: birth details and contact
 * values cannot enter this event by construction.
 */
export function trackKundaliFormStep(props: {
  step: KundaliStep;
  action: KundaliStepAction;
  occurrence: number;
  language: string;
  source?: string;
  source_path?: string;
}) {
  trackEventLazy("kundali_form_step", props);
}

// ── Field-level reliability funnel (rule 34 owned beacon → events_raw) ───────
// The 31,749 form-failure events (birthPlace 15,124 / birthDate 8,684 /
// API-unreachable 5,150) were invisible because `kundali_form_submit_failed`
// CONFLATES three different failures: a user who never engaged a field, a city
// that was genuinely ambiguous, and a true network error. These three events
// split that apart so a dbt mart over events_raw can finally tell ambiguity from
// error from network, PER FIELD. `kundali_form_submit_failed` is kept above for
// back-compat; these are additive, never a replacement.

export type KundaliField = "name" | "birthDate" | "birthTime" | "birthPlace" | "phone" | "email";
export type KundaliFieldPhase = "start" | "valid";
/**
 * Per-field terminal outcome at submit time:
 *  - ok           — field had a valid value
 *  - ambiguous    — birthPlace typed but could not resolve to a confident city
 *  - empty        — required field left blank / never engaged
 *  - network_error— birthPlace resolution failed on a network/transport error
 *  - invalid      — field had a malformed value (bad date, bad phone, bad email)
 */
export type KundaliFieldOutcome =
  "ok" | "ambiguous" | "empty" | "no_match" | "network_error" | "invalid";

/** field_start → field_valid transition for one field (per render-stable). */
export function trackKundaliFieldFunnel(props: {
  field: KundaliField;
  phase: KundaliFieldPhase;
  source?: string;
  source_path?: string;
  language?: string;
}) {
  trackEventLazy("kundali_field_funnel", props);
}

/** Terminal per-field outcome at submit (splits ambiguity vs error vs empty). */
export function trackKundaliFieldOutcome(props: {
  field: KundaliField;
  outcome: KundaliFieldOutcome;
  source?: string;
  source_path?: string;
  language?: string;
}) {
  trackEventLazy("kundali_field_outcome", props);
}

/** One event per kundali-API attempt so unreachable% of submits is measurable. */
export function trackKundaliApiAttempt(props: {
  attempt_id?: string;
  form_id?: string;
  attempt: number;
  outcome: "ok" | "retry_4xx_skip" | "retry_network" | "retry_5xx";
  source?: string;
  source_path?: string;
  language?: string;
}) {
  trackEventLazy("kundali_api_attempt", props);
}

export function trackKundaliResultViewed(props: {
  attempt_id?: string;
  form_id?: string;
  reading_id?: string;
  language: string;
  has_time: boolean;
  source?: string;
  source_path?: string;
  dosha_count?: number;
  product_count?: number;
  interpretation_quality?: "personalized" | "engine_fallback" | "empty";
}) {
  trackEventLazy("kundali_result_viewed", props);
}

export function trackKundaliGenerated(props: {
  attempt_id?: string;
  form_id?: string;
  reading_id?: string;
  language: string;
  has_time: boolean;
  source?: string;
  source_path?: string;
}) {
  trackEventLazy("kundali_generated", props);
  trackFreeToolCompleted({
    tool_name: "free-kundali",
    form_id: props.form_id,
    attempt_id: props.attempt_id,
    reading_id: props.reading_id,
    language: props.language,
    source_path: props.source_path,
    has_time: props.has_time,
  });
}
